import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * The FPV grass floor. Everything is generated — a tileable grass canvas
 * texture (moss patches + angled blade strokes, wrap-drawn across the seams)
 * on a big plane that FOLLOWS the camera in whole-tile steps, so it extends to
 * the horizon in every direction without the texture ever appearing to move.
 * Inherently bright green; the Darkness dial multiplies it down (0 = noon
 * lawn, 1 = lights out). Edges fade into the night sky relative to the camera.
 * Opaque, so it renders before the additive drone points and writes depth
 * (below-horizon stars vanish behind it).
 */

const SIZE = 1000;   // plane span — past the 400-unit camera far plane in all directions
const REPEAT = 200;  // → 5-unit (~6 m) tiles; whole-tile snapping keeps uv continuous

function makeGrassTexture(): THREE.CanvasTexture {
  const S = 256;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const ctx = cv.getContext("2d")!;
  const WRAP: [number, number][] = [[0, 0], [S, 0], [-S, 0], [0, S], [0, -S]];

  // healthy lawn base
  ctx.fillStyle = "#1e5c22";
  ctx.fillRect(0, 0, S, S);

  // low-frequency moss/patch mottling
  for (let i = 0; i < 260; i++) {
    const x = Math.random() * S, y = Math.random() * S, r = 4 + Math.random() * 14;
    const g = 70 + Math.random() * 60;
    ctx.fillStyle = `rgba(${(g * 0.3) | 0}, ${g | 0}, ${(g * 0.32) | 0}, ${0.12 + Math.random() * 0.16})`;
    for (const [ox, oy] of WRAP) {
      ctx.beginPath();
      ctx.arc(x + ox, y + oy, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // grass blades: short near-vertical strokes, light and dark greens mixed
  for (let i = 0; i < 1700; i++) {
    const x = Math.random() * S, y = Math.random() * S;
    const len = 3 + Math.random() * 5;
    const a = -Math.PI / 2 + (Math.random() - 0.5) * 1.1;
    const g = 90 + Math.random() * 120;
    ctx.strokeStyle = `rgba(${(g * 0.32) | 0}, ${g | 0}, ${(g * 0.3) | 0}, ${0.25 + Math.random() * 0.35})`;
    ctx.lineWidth = 0.7 + Math.random() * 0.5;
    for (const [ox, oy] of WRAP) {
      ctx.beginPath();
      ctx.moveTo(x + ox, y + oy);
      ctx.lineTo(x + ox + Math.cos(a) * len, y + oy + Math.sin(a) * len);
      ctx.stroke();
    }
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8; // grazing FPV angles smear badly without it
  return tex;
}

const VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorld;
  void main() {
    vUv = uv;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;

// coarse second sample of the same texture breaks up visible tiling; the fade
// is camera-relative so the horizon ring travels with the pilot
const FRAG = /* glsl */ `
  uniform sampler2D uTex;
  uniform float uRepeat;
  uniform float uDark;
  uniform vec3 uBg;
  varying vec2 vUv;
  varying vec3 vWorld;
  void main() {
    vec3 g = texture2D(uTex, vUv * uRepeat).rgb;
    float patch = texture2D(uTex, vUv * 5.0).g;
    g *= 0.72 + patch * 0.9;
    g *= 1.0 - uDark;
    float d = length(vWorld.xz - cameraPosition.xz);
    float fade = 1.0 - smoothstep(60.0, 340.0, d);
    gl_FragColor = vec4(mix(uBg, g, fade), 1.0);
  }
`;

export function GroundPlane({ y, dark }: { y: number; dark: number }) {
  const meshRef = useRef<THREE.Mesh>(null);
  const tex = useMemo(makeGrassTexture, []);
  useEffect(() => () => tex.dispose(), [tex]);
  const uniforms = useMemo(
    () => ({
      uTex: { value: tex },
      uRepeat: { value: REPEAT },
      uDark: { value: 0.35 },
      uBg: { value: new THREE.Color("#03040a") },
    }),
    [tex]
  );
  useEffect(() => { uniforms.uDark.value = THREE.MathUtils.clamp(dark, 0, 1); }, [uniforms, dark]);

  // infinite floor: track the camera in whole-tile steps (uv is plane-local, so
  // snapped motion is invisible — the grass appears fixed to the world)
  useFrame(({ camera }) => {
    const m = meshRef.current;
    if (!m) return;
    const tile = SIZE / REPEAT;
    m.position.x = Math.round(camera.position.x / tile) * tile;
    m.position.z = Math.round(camera.position.z / tile) * tile;
  });

  return (
    <mesh ref={meshRef} position={[0, y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[SIZE, SIZE]} />
      <shaderMaterial vertexShader={VERT} fragmentShader={FRAG} uniforms={uniforms} />
    </mesh>
  );
}
